import React, { useEffect, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { IScore, IBarnHuntScore } from "@eagerdog/interfaces";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import StopWatch from "src/components/EnterResults/StopWatch/StopWatch";

interface IProps {
  result: IScore;
  onChange(result: IBarnHuntScore): void;
}

/**
 * Line Drive Results Form
 * - Time only (no Pass/Fail toggle)
 * - Placements across all heights based on time
 * - "No Time" if dog doesn't finish
 */
const LineDriveForm: React.FC<IProps> = (props) => {
  const [time, setTime] = useState<string>("0:00:00");
  const [finishedCourse, setFinishedCourse] = useState<IOption>({ value: "Yes", id: "yes" });
  const [loaded, setLoaded] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const finishedOptions: IOption[] = [
    { id: "yes", value: "Yes" },
    { id: "no", value: "No (No Time)" },
    { id: "absent", value: "Absent" },
  ];

  useEffect(() => {
    if (!loaded) {
      if (props.result && props.result.barn_hunt) {
        const { result_type, time: savedTime } = props.result.barn_hunt;
        
        if (result_type === Constants.score_result_type.absent) {
          setFinishedCourse({ id: "absent", value: "Absent" });
        } else if (savedTime) {
          setFinishedCourse({ id: "yes", value: "Yes" });
          // Stored as MM:SS.TT, but StopWatch expects MM:SS:TT
          setTime(savedTime.replace(".", ":"));
        } else {
          setFinishedCourse({ id: "no", value: "No (No Time)" });
        }
      }
      setLoaded(true);
    }
  }, [loaded, props.result]);

  useEffect(() => {
    if (hasChanged) {
      let resultType: string;
      let resultTime: string | undefined;

      if (finishedCourse.id === "absent") {
        resultType = Constants.score_result_type.absent;
        resultTime = undefined;
      } else if (finishedCourse.id === "no") {
        resultType = Constants.score_result_type.not_qualified;
        resultTime = undefined;
      } else {
        resultType = Constants.score_result_type.qualified;
        // Convert from M:SS:TT to MM:SS.TT format for storage
        const parts = time.split(":");
        if (parts.length === 3) {
          resultTime = `${parts[0]}:${parts[1]}.${parts[2]}`;
        }
      }

      props.onChange({
        result_type: resultType,
        time: resultTime,
      });

      setHasChanged(false);
    }
  }, [hasChanged, time, finishedCourse, props]);

  const handleTimeChange = (value: string) => {
    setTime(value);
    setHasChanged(true);
  };

  const handleFinishedChange = (e: any, value: IOption) => {
    setFinishedCourse(value);
    setHasChanged(true);
  };

  return (
    <div className="BarnHuntResults">
      <div className="instruction-section">
        <p className="instruction-text">
          <strong>Line Drive:</strong> Enter time (MM:SS.00). Dogs are placed by time across all heights.
        </p>
      </div>

      <div className="form-row">
        <Dropdown
          value={finishedCourse}
          label="Did the dog finish the course?"
          onChange={handleFinishedChange}
          options={finishedOptions}
          placeholder=""
        />
      </div>

      {finishedCourse.id === "yes" && (
        <div className="form-row">
          <StopWatch
            time={time}
            onChange={handleTimeChange}
            label="Time"
          />
        </div>
      )}
    </div>
  );
};

export default LineDriveForm;

